import {
  getMarketingEventById,
  isMarketingEventConsumed,
  isMarketingEventFinished,
} from 'src/replicant/marketing/marketing.getters';
import StateObserver, { user } from 'src/StateObserver';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import {
  trackMarketingEventCollectError,
  trackMarketingRewardCollected,
} from 'src/lib/analytics/events';
import { captureGenericError } from 'src/lib/sentry';
import { setServerMarketingEvents } from 'src/replicant/ruleset/airtable';
import * as Tutorial from 'src/replicant/tutorial';
import i18n from 'src/lib/i18n/i18n';
import { getEntryData } from 'src/lib/platformUtils';
import { withLoading } from 'src/lib/utils';
import { Feature } from 'src/replicant/analytics';

/**
 * Called on game launch, show and consume marketing events rewards
 */
export async function marketingEventsSequence() {
  if (!Tutorial.getters.isTutorialStepComplete(user(), 'basicGameplay')) {
    return false; // Skip the sequence in tutorial.
  }

  // Get post id payload
  const entryPostId = getEntryData().$pageLinkId;

  // User not entered through post
  if (!entryPostId) {
    return false;
  }

  // Fetch marketing events from airtable.
  try {
    const marketingEvents = await withLoading(
      () => StateObserver.replicant.asyncGetters.fetchMarketingEvents(),
      {
        feature: Feature.MARKETING._,
        subFeature: Feature.MARKETING.FETCH_EVENTS,
      },
    );

    setServerMarketingEvents(marketingEvents);
  } catch (e) {
    captureGenericError('Could not fetch marketing events.', e);
  }

  // Check postId and give reward or show error if checks failed
  const now = StateObserver.now();
  const state = StateObserver.getState().user;

  // Check if we have event
  const event = getMarketingEventById(entryPostId);

  // No event with this id, silently skip
  if (!event) {
    // Suppress error tracking to avoid spamming Sentry with it for short link ids that are not meant to be marketing events.
    // See spec: https://docs.google.com/document/d/1X0zW2Gh76KkyKc91riCIhjH6Gb0Uf3nhU0W4jGQXmH8/edit?tab=t.0
    // Discussion: https://playcoinc.slack.com/archives/C04ALB20URL/p1772095260726669
    // Concern: https://playcoinc.slack.com/archives/C04ALB20URL/p1772099451764199?thread_ts=1772095260.726669&cid=C04ALB20URL
    // trackMarketingEventCollectError(entryPostId, 'wrong-id');
    return false;
  }

  // No rewards, no need to do anything
  // NB: make sure this check is valid when updating MarketingEventReward
  if (Object.keys(event.rewards).length === 0) {
    return false;
  }

  // If event finished or not last exit
  if (isMarketingEventFinished(event.id, now)) {
    await openPopupPromise('popupModalWhite', {
      title: i18n('marketing.sorry'),
      message: i18n('marketing.expired'),
      tracking: { feature: 'marketing', $subFeature: 'expired' },
    });
    trackMarketingEventCollectError(event.id, 'expired');
    return false;
  }

  // User already got reward for this post
  if (isMarketingEventConsumed(state, event.id)) {
    await openPopupPromise('popupModalWhite', {
      title: i18n('marketing.sorry'),
      message: i18n('marketing.invalidId'),
      tracking: { feature: 'marketing', $subFeature: 'already-received' },
    });
    trackMarketingEventCollectError(event.id, 'already-received');
    return false;
  }

  // All checks done, show success popup and consume reward
  await openPopupPromise('popupMarketingRewards', { eventId: event.id });

  await StateObserver.invoke.consumeMarketingReward({ eventId: event.id });

  // Track collect analytics event
  trackMarketingRewardCollected(event.id, event.rewards);

  return false;
}
